/**
 * §7.7 Platform Launch Timeline — interactive 8-step expandable
 * timeline.  Click any step to see the deeper detail.  Similar
 * pattern to platform/sections/WhiteLabelInfra but with a persistent
 * horizontal rail on desktop.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import {
  ShoppingCart, CreditCard, ServerCog, Palette, Globe, ToggleRight,
  UsersRound, Rocket, Sparkles, ChevronDown,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { LAUNCH_TIMELINE } from '../data'
import { TONE } from '../_shared'

const ICONS = { ShoppingCart, CreditCard, ServerCog, Palette, Globe, ToggleRight, UsersRound, Rocket }

export default function Timeline() {
  const [openId, setOpenId] = useState(LAUNCH_TIMELINE[0]?.id)

  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Platform Launch Timeline</Eyebrow>
          <SectionHeading align="center"
            title={<>Eight steps.{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">One afternoon.</span></>}
            subtitle="From purchase to public launch — every step of provisioning is automated. Click any step to expand its deeper detail."
          />
        </div>

        {/* Desktop horizontal flow */}
        <div className="hidden lg:block relative">
          <svg viewBox="0 0 1400 220" className="w-full h-auto">
            <defs>
              <linearGradient id="lt-line" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="#FFC107" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0.4" />
              </linearGradient>
            </defs>
            <path id="lt-path" d="M 60 110 L 1340 110" stroke="url(#lt-line)" strokeWidth="2" strokeDasharray="4 8" fill="none" className="animate-brand-dash" />
            <circle r="4" fill="#FFC107">
              <animateMotion dur="12s" repeatCount="indefinite"><mpath href="#lt-path" /></animateMotion>
            </circle>
            {LAUNCH_TIMELINE.map((n, i) => {
              const x = 90 + (1220 * i) / (LAUNCH_TIMELINE.length - 1)
              const tone = TONE[n.tone] || TONE.orange
              const Icon = ICONS[n.iconKey] || Sparkles
              return (
                <g key={n.id} transform={`translate(${x} 110)`}>
                  <circle r="28" fill="rgba(10,10,12,0.9)" stroke={tone.hex} strokeOpacity="0.55" strokeWidth="1.4" />
                  <circle r="25" fill={`${tone.hex}12`} />
                  <foreignObject x="-13" y="-13" width="26" height="26">
                    <div className="flex items-center justify-center w-full h-full" style={{ color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 13, height: 13 }} />
                    </div>
                  </foreignObject>
                  <text y="52" textAnchor="middle" fontSize="10" fontWeight="600" fill="rgba(255,255,255,0.9)">{n.label}</text>
                  <text y="66" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="rgba(255,255,255,0.35)">{String(i + 1).padStart(2, '0')}</text>
                </g>
              )
            })}
          </svg>
        </div>

        {/* Expandable detail grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {LAUNCH_TIMELINE.map((n, i) => {
            const tone = TONE[n.tone] || TONE.orange
            const Icon = ICONS[n.iconKey] || Sparkles
            const open = openId === n.id
            return (
              <button
                key={n.id}
                onClick={() => setOpenId(open ? null : n.id)}
                aria-expanded={open}
                className={clsx(
                  'group relative text-left rounded-2xl border p-4 transition-all overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60',
                  open
                    ? 'border-white/20 bg-gradient-to-b from-white/[0.05] to-white/[0.01] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.9)]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.03]',
                )}
              >
                {open && (
                  <div className="pointer-events-none absolute inset-0 opacity-70"
                    style={{ background: `radial-gradient(70% 45% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                )}
                <div className="relative flex items-center gap-2 mb-1.5">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon style={{ width: 11, height: 11 }} strokeWidth={2} />
                  </span>
                  <span className="text-[9.5px] font-mono uppercase tracking-wider" style={{ color: tone.hex }}>step {String(i + 1).padStart(2, '0')}</span>
                  <ChevronDown className={clsx('ml-auto h-3.5 w-3.5 text-zinc-500 transition-transform', open && 'rotate-180')} />
                </div>
                <div className="relative text-[13.5px] font-semibold text-white leading-tight">{n.label}</div>
                <p className="relative text-[12px] text-zinc-400 leading-relaxed mt-1.5">{n.summary}</p>
                {open && (
                  <p className="relative text-[12px] text-zinc-200 leading-relaxed mt-2 pt-2 border-t border-white/5">
                    {n.body}
                  </p>
                )}
              </button>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
